<script setup lang="ts">
import { useImage } from '@vueuse/core';
import type { Image } from '~/types/base/image';
import type { StrapiMediaFile } from '~/types';

defineOptions({
  inheritAttrs: false,
});

interface Props {
  data: Image | StrapiMediaFile;
  rounded?: '2xl' | true | '' | '3xl' | 'full';
  skeleton?: boolean;
  stretch?: boolean;
  maxHeight?: string;
  ratio?: string;
  link?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  skeleton: true,
  rounded: undefined,
  stretch: false,
  maxHeight: undefined,
  ratio: undefined,
  link: true,
});

const NuxtLinkLocale = resolveComponent('NuxtLinkLocale');

const { getCompleteUrl } = useCompleteStrapiUrl();

const isStrapiImage = computed(() => {
  return !Object.keys(props.data).includes('source');
});

const linkUrl = computed(() => {
  if (isStrapiImage.value) return null;

  return (props.data as Image).link?.url;
});
const linkTitle = computed(() => {
  if (isStrapiImage.value) return null;

  return (props.data as Image).link?.title;
});
const linkTarget = computed(() => {
  if (isStrapiImage.value) return null;

  return (props.data as Image).link?.targetBlank ? '_blank' : null;
});

const imageData = computed(() =>
  isStrapiImage.value
    ? (props.data as StrapiMediaFile)
    : (props.data as Image).source,
);
const src = computed(() => getCompleteUrl(imageData.value.url));
const alt = computed(() => imageData.value.alternativeText);
const title = computed(() => imageData.value.caption);

const formats = computed(() => imageData.value.formats);
const isFormatsEmpty = computed(() => {
  return !formats.value || !formats.value.medium || !formats.value.large;
});

const MOBILE_MAX_WIDTH = 744;
const TABLET_MAX_WIDTH = 1024;

const sources = computed(() => {
  if (isFormatsEmpty.value) return {};

  return {
    medium: {
      media: `(max-width: ${MOBILE_MAX_WIDTH}px)`,
      url: getCompleteUrl(formats.value!.medium.url),
    },
    large: {
      media: `(max-width: ${TABLET_MAX_WIDTH}px)`,
      url: getCompleteUrl(formats.value!.large.url),
    },
  };
});

const { isMobile } = useGetDevice();
const { width } = useWindowSize();
const currentImage = computed(() => {
  if (import.meta.server && !isFormatsEmpty.value && isMobile) {
    return formats.value!.medium;
  }

  if (import.meta.client && !isFormatsEmpty.value) {
    if (width.value <= MOBILE_MAX_WIDTH) return formats.value!.medium;
    if (width.value <= TABLET_MAX_WIDTH) return formats.value!.large;
  }

  return imageData.value;
});

const isMounted = useMounted();

const currentImageRatio = computed(() => {
  if (props.ratio) return props.ratio === 'golden' ? '1.618/1' : props.ratio;

  if (
    isMounted.value &&
    currentImage.value.width &&
    currentImage.value.height
  ) {
    return `${currentImage.value.width}/${currentImage.value.height}`;
  }
  return 'auto';
});

const styles = computed(() => {
  return {
    aspectRatio: currentImageRatio.value,
    width: '100%',
    maxHeight: props.maxHeight,
  };
});

const skeletonStyles = computed(() => {
  if (props.maxHeight || props.stretch) {
    return styles.value;
  } else {
    return {
      ...styles.value,
      width: '100%',
      maxHeight: isMounted.value ? `${currentImage.value.height}px` : undefined,
      maxWidth: isMounted.value ? `${currentImage.value.width}px` : undefined,
    };
  }
});

const roundedClasses = computed(() => {
  return {
    'overflow-hidden': props.rounded || props.rounded === '',
    'rounded-3xl-smooth':
      props.rounded === true || props.rounded === '' || props.rounded === '3xl',
    'rounded-2xl-smooth': props.rounded === '2xl',
    'rounded-full': props.rounded === 'full',
  };
});

const containerClasses = computed(() => {
  return {
    'h-full w-full': props.stretch,
    ...roundedClasses.value,
  };
});

const useImageOptions = computed(() => ({
  src: getCompleteUrl(currentImage.value.url),
}));
const { isLoading } = useImage(useImageOptions);
</script>

<template>
  <Transition name="fade" mode="out-in">
    <div
      v-if="isLoading"
      :class="{
        'skeleton-loader': skeleton,
        ...roundedClasses,
      }"
      :style="skeletonStyles"
      v-bind="$attrs"
    />
    <div
      v-else
      :aria-hidden="!linkUrl"
      :class="containerClasses"
      v-bind="$attrs"
    >
      <component
        :is="linkUrl && link ? NuxtLinkLocale : 'div'"
        tabindex="-1"
        :to="linkUrl"
        :title="linkTitle"
        :aria-label="linkTitle"
        :target="linkTarget"
        class="h-full w-full"
        data-testid="image-container"
      >
        <picture>
          <source
            v-for="(source, index) in sources"
            :key="index"
            :srcset="source?.url"
            :media="source?.media"
          />
          <img
            :class="{
              'h-full w-full': stretch,
              'object-cover': stretch || ratio,
            }"
            :style="styles"
            :src
            :alt
            :title
            draggable="false"
            data-testid="image-element"
          />
        </picture>
      </component>
    </div>
  </Transition>
</template>
